import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import startsWith from 'lodash/startsWith';
import { resetSearchSuggestions, triggerSearchPage, triggerSearchSuggestions } from '../../store/search/actions';
import { SearchSuggestions } from './SearchSuggestions';
import { SearchScope } from '../../store/search/state';
import { AppDispatch } from '../../store';
import { OrderListState } from '../../store/orderlist/state';
import { Orderlist } from '../../../models/OrderList';
import { ProductSearchResponseOut, SingleActionResponse, TopHitOut } from '../../store/api';
import { AppState } from '../../store/appState';
import RoutingUtils from '../utils/utils-routing';
import { DropDownMenu } from '../common/DropDownMenu';
import { useClickOutside } from '../hooks/useClickOutside';
import { useDebouncedCallback } from '../hooks/useDebouncedCallback';
import './SearchBar.scss';
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import { UpdateProductOptions } from '../../store/products/actionsCart';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../common/useListContentChecks';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { useParams } from 'react-router';
import { ShopLanguageScopeDropDown } from './ShopLanguageScopeDropdown';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';

type OwnProps = {
    readonly orderlist: OrderListState;
    readonly orderlists: ReadonlyArray<Orderlist>;
    readonly onOpenSearchBar: () => void;
    readonly onCloseSearchBar: () => void;
    readonly searchScope: SearchScope;
    readonly searchScopes: SearchScope[];
    readonly isSticky: boolean;
    readonly btnClassName?: string;
    readonly searchBarActive: boolean;
    readonly searchQuery: string;
    readonly onSearchQueryChange: (newQuery: string) => void;
    readonly onAddToCart: (product: UpdateProductOptions, performChecks: boolean) => Promise<SingleActionResponse>;
    readonly onAddToList: (
        product: UpdateProductOptions,
        listId: string,
        performChecks: boolean
    ) => Promise<SingleActionResponse>;
    readonly changeSearchScope: (newScope: SearchScope) => void;
    readonly searchLabel: string;
    readonly getSearchPath: (scopeKey: string) => string;
    readonly isInYourBorrowBox: boolean;
};

type StateProps = {
    readonly searchSuggestions?: { [key: string]: ProductSearchResponseOut };
    readonly searchSuggestionTopHits: ReadonlyArray<TopHitOut> | undefined;
};

type Actions = {
    readonly fetchSearchSuggestions: (query: string, scope: SearchScope, shopLanguage?: string) => void;
    readonly resetSearchSuggestions: () => void;
};

type Props = OwnProps & StateProps & Actions;

const SearchBar: FC<Props> = ({
    searchBarActive,
    searchScope,
    searchQuery,
    isSticky,
    onSearchQueryChange,
    onOpenSearchBar,
    onCloseSearchBar,
    resetSearchSuggestions,
    fetchSearchSuggestions,
    changeSearchScope,
    searchScopes,
    searchLabel,
    getSearchPath,
    isInYourBorrowBox,
    onAddToCart,
    onAddToList,
    ...props
}) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const searchRef = useRef<HTMLDivElement>(null);
    const searchQueryRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const showSuggestions = searchQuery.length > 0;
    const { t } = useTranslation();
    const { shopLanguage } = useParams<{ shopLanguage?: string }>();
    const [selectedShopLanguage, setSelectedShopLanguage] = useState<string | undefined>(
        isMultiLanguageShopEnabled ? (!shopLanguage || shopLanguage === 'featured' ? 'all' : shopLanguage) : undefined
    );

    const closeSearchBar = useCallback(
        (clear?: boolean) => {
            searchQueryRef.current?.blur();
            if (clear) {
                resetSearchSuggestions();
                onSearchQueryChange('');
            }
            onCloseSearchBar();
        },
        [resetSearchSuggestions, onSearchQueryChange, onCloseSearchBar]
    );

    useClickOutside({
        element: searchRef,
        handler: closeSearchBar,
        ignoreElement: suggestionsRef,
        enabled: !showSuggestions
    });

    const activateSearchForm = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            if (searchBarActive) {
                return;
            }
            onOpenSearchBar();
        },
        [searchBarActive, onOpenSearchBar]
    );

    useEffect(() => {
        if (searchBarActive) {
            searchQueryRef.current?.focus();
        }
    }, [searchBarActive]);

    useEffect(() => {
        if (!isMultiLanguageShopEnabled) {
            return;
        }

        if (!shopLanguage || shopLanguage === 'featured') {
            setSelectedShopLanguage('all');
        } else {
            setSelectedShopLanguage(shopLanguage);
        }
    }, [isMultiLanguageShopEnabled, shopLanguage]);

    const [fetchSuggestions] = useDebouncedCallback(fetchSearchSuggestions, 420);

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return onAddToList(options.options, options.listId, performChecks);
            } else {
                return onAddToCart(options.options, performChecks);
            }
        },
        [onAddToCart, onAddToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const onChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newQuery = event.target.value;
            fetchSuggestions(newQuery, searchScope, selectedShopLanguage);
            onSearchQueryChange(newQuery);
        },
        [fetchSuggestions, searchScope, selectedShopLanguage, onSearchQueryChange]
    );

    const onAdvancedSearch = useCallback(() => {
        searchQueryRef.current?.blur();

        const query = {
            q: searchQuery,
            scope: searchScope.key,
            shopLanguage: selectedShopLanguage,
            displayFilter: null
        };

        RoutingUtils.pushUrlWithQuery(getSearchPath(searchScope.key), query);
        closeSearchBar();
    }, [closeSearchBar, getSearchPath, searchQuery, searchScope.key, selectedShopLanguage]);

    const onSubmit = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();

            const scopeKey = searchScope.key;
            const query = {
                q: searchQuery,
                scope: scopeKey,
                shopLanguage: selectedShopLanguage
            };

            RoutingUtils.pushUrlWithQuery(getSearchPath(scopeKey), query);

            resetSearchSuggestions();
            closeSearchBar();
        },
        [closeSearchBar, getSearchPath, resetSearchSuggestions, searchQuery, searchScope.key, selectedShopLanguage]
    );

    const handleSearchScopeChange = useCallback(
        (newScope: SearchScope) => {
            changeSearchScope(newScope);
            fetchSuggestions(searchQuery, newScope, selectedShopLanguage);
            searchQueryRef.current?.focus();
        },
        [changeSearchScope, fetchSuggestions, searchQuery, selectedShopLanguage]
    );

    const handleShopLanguageChange = useCallback(
        (lng: string) => {
            setSelectedShopLanguage(lng);
            fetchSuggestions(searchQuery, searchScope, lng);
            searchQueryRef.current?.focus();
        },
        [fetchSuggestions, searchQuery, searchScope]
    );

    const onShowAllOfLoanformat = useCallback(
        (scopeKey: string) => {
            closeSearchBar();
            triggerSearchPage({ q: searchQuery, scope: scopeKey, shopLanguage: selectedShopLanguage });
        },
        [closeSearchBar, searchQuery, selectedShopLanguage]
    );

    const onShowAllOfScope = useCallback(
        (scope: SearchScope) => {
            closeSearchBar();
            triggerSearchPage({ q: searchQuery, scope: scope.key, shopLanguage: selectedShopLanguage });
        },
        [closeSearchBar, searchQuery, selectedShopLanguage]
    );

    const placeholderText = searchQuery || searchLabel;

    return (
        <div className="searchbar-container">
            {!isSticky && (
                <div className="placeholder" onClick={activateSearchForm}>
                    <i className="material-icons" style={{ height: '100%' }}>
                        search
                    </i>
                    &nbsp;<span>{placeholderText}</span>
                </div>
            )}
            <CSSTransition in={searchBarActive} timeout={300} mountOnEnter={true} unmountOnExit={true}>
                <div ref={searchRef} className={classNames('searchbar', searchBarActive && 'expanded')}>
                    {selectedShopLanguage && !isInYourBorrowBox ? (
                        <ShopLanguageScopeDropDown
                            currentShopLanguage={selectedShopLanguage}
                            selectLanguageScope={handleShopLanguageChange}
                        />
                    ) : null}
                    <SearchScopeDropDown
                        currentScope={searchScope}
                        scopes={searchScopes}
                        onChange={handleSearchScopeChange}
                        isInYourBorrowBox={isInYourBorrowBox}
                    />
                    <form className="search-form" onSubmit={onSubmit} onClick={activateSearchForm}>
                        <input
                            type="text"
                            ref={searchQueryRef}
                            value={searchQuery}
                            placeholder={t('components.searchBar.placeholder')}
                            onChange={onChange}
                            maxLength={1500}
                        />
                    </form>
                    <a className="clear-action">
                        <i className="material-icons" onClick={() => closeSearchBar(true)}>
                            clear
                        </i>
                    </a>
                    <a className="advanced-search" onClick={onAdvancedSearch}>
                        <span>{t('components.searchBar.advancedSearch')}</span>
                        <i className="material-icons">arrow_forward</i>
                    </a>
                </div>
            </CSSTransition>
            {searchBarActive && (
                <SearchSuggestions
                    orderlists={props.orderlists}
                    onAddPrice={changePrice}
                    orderlist={props.orderlist}
                    searchSuggestions={props.searchSuggestions}
                    searchSuggestionTopHits={props.searchSuggestionTopHits}
                    scrollInnerRef={suggestionsRef}
                    onShowAllOfLoanformat={onShowAllOfLoanformat}
                    onShowAllOfScope={onShowAllOfScope}
                    deactivateSearchForm={closeSearchBar}
                    closeSuggestions={closeSearchBar}
                    hideSuggestions={!showSuggestions}
                    searchQuery={searchQuery}
                    searchScope={searchScope}
                    shopLanguage={selectedShopLanguage}
                />
            )}
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </div>
    );
};

function mapStateToProps(state: AppState): StateProps {
    return {
        searchSuggestions: state.search.suggestions ?? {},
        searchSuggestionTopHits: state.search.topHits
    };
}

function mapDispatchToActions(dispatch: AppDispatch): Actions {
    return {
        fetchSearchSuggestions: (query, scope, shopLanguage) =>
            dispatch(triggerSearchSuggestions(query, scope, shopLanguage)),
        resetSearchSuggestions: () => dispatch(resetSearchSuggestions())
    };
}

export default connect(mapStateToProps, mapDispatchToActions, null, { forwardRef: true })(SearchBar);

type SearchScopeDropDownProps = {
    readonly currentScope: SearchScope;
    readonly scopes: ReadonlyArray<SearchScope>;
    readonly onChange: (newScope: SearchScope) => void;
    readonly className?: string;
    readonly isInYourBorrowBox: boolean;
};

const SearchScopeDropDown: FC<SearchScopeDropDownProps> = ({
    currentScope,
    scopes,
    onChange,
    className,
    isInYourBorrowBox
}) => {
    const { t } = useTranslation();
    const currentScopeLabel = currentScope
        ? isInYourBorrowBox
            ? t(`components.searchScope.collection.${currentScope.key.toLowerCase()}`, {
                  defaultValue: currentScope.name
              })
            : t(`components.searchScope.shop.${currentScope.key.toLowerCase()}`, {
                  defaultValue: currentScope.name
              })
        : '';

    const [expanded, setExpanded] = useState(false);

    return (
        <DropDownMenu
            iconCode="search"
            iconSizeClass="icn-search"
            className={classNames('search-scope-dropdown', 'fl-none', className)}
            expanded={expanded}
            onToggleClick={setExpanded}
            onClickOutside={setExpanded}
            align="left"
            gap={10}
            label={currentScopeLabel}
        >
            <ul>
                {scopes.map(scope => {
                    const loanFormatKey = scope.key.toLowerCase();
                    const replacedString = loanFormatKey.replace('_', ' ');
                    const isActive = startsWith(currentScope.key.toLowerCase(), replacedString);
                    const highlightClass = isActive ? 'checked' : undefined;
                    const check = isActive ? <i className="material-icons">check</i> : null;
                    return (
                        <li key={loanFormatKey} className="search-scope-dropdown-item">
                            <a
                                className={highlightClass}
                                onClick={() => {
                                    onChange(scope);
                                    setExpanded(false);
                                }}
                            >
                                {isInYourBorrowBox
                                    ? t(`components.searchScope.collection.${loanFormatKey}`, {
                                          defaultValue: scope.name
                                      })
                                    : t(`components.searchScope.shop.${loanFormatKey}`, { defaultValue: scope.name })}
                                {check}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </DropDownMenu>
    );
};
